import { FormEvent, useState } from 'react';
import useTableOrder from 'hooks/tableOrder';
import { Procedure } from 'types/procedure';
import { api } from 'services/api';
import { Paginated } from 'types/paginated';
import { moneyFormat, percentFormat } from 'helpers/moneyFormat';

export type ProcedureQueryParams = {
  active: boolean;
  name: string;
};

export function useFetchProcedures() {
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState<number>(96);
  const [total, setTotal] = useState<number>(0);
  const [orderedIndex, sort] = useTableOrder();
  const [filter, setFilter] = useState<ProcedureQueryParams>({
    name: '',
    active: true,
  });

  function fetchData(nextPage = page, nextRows = rows, nextFilter = filter) {
    setLoading(true);

    api
      .get<Paginated<Procedure[]>>('/procedures', {
        params: {
          ...nextFilter,
          active: nextFilter.active ? 1 : 0,
          page: nextPage + 1,
          rows: nextRows,
          type: 'G',
        },
      })
      .then(response => {
        setTotal(response.data.total);
        const _procedures = response.data.items.map(procedure => ({
          ...procedure,
          formattedActive: procedure.active ? 'Sim' : 'Não',
          formattedProfitMargin: percentFormat(procedure.profit_margin),
          formattedSalePrice: moneyFormat(procedure.sale_price),
          formattedCommission: moneyFormat(procedure.commission),
          formattedCost: moneyFormat(procedure.cost),
        }));
        setProcedures(_procedures);
      })
      .finally(() => setLoading(false));
  }

  function handleSearch(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setPage(0);
    fetchData(0, rows, filter);
  }

  function handleChangePage(nextPage: number) {
    setPage(nextPage);
    fetchData(nextPage, rows, filter);
  }

  function handleChangeRows(nextRows: number) {
    setRows(nextRows);
    setPage(0);
    fetchData(0, nextRows, filter);
  }

  function handleSort(index: string) {
    const p = sort(index, procedures);
    setProcedures(p);
  }

  function handleChange(index: keyof ProcedureQueryParams, value: string) {
    setFilter(state => ({
      ...state,
      [index]: value,
    }));
  }

  return {
    procedures,
    loading,
    orderedIndex,
    handleSort,
    filter,
    handleChange,
    setPage: handleChangePage,
    setRows: handleChangeRows,
    handleSearch,
    page,
    rows,
    total,
  };
}
